import React, { useState, useEffect } from 'react';
import {
  Container,
  Card,
  Row,
  Col,
  Button,
  Form,
  Alert,
  Spinner,
  Modal,
  Tabs,
  Tab,
  InputGroup,
  ButtonGroup,
  Dropdown,
  Button as BootstrapButton
} from 'react-bootstrap';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { fires } from '../../firebase';
import { FaSave, FaEye, FaDownload, FaUpload, FaBold, FaItalic, FaUnderline, FaAlignLeft, FaAlignCenter, FaAlignRight, FaListUl, FaListOl, FaLink, FaImage, FaHeading, FaCode, FaUndo, FaRedo } from 'react-icons/fa';

export default function EmailTemplateCreator() {
  const navigate = useNavigate();
  const { currentUser } = useAuth();
  
  // State management
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [isAdmin, setIsAdmin] = useState(false);
  const [saving, setSaving] = useState(false);
  
  // Template data
  const [templates, setTemplates] = useState([]);
  const [currentTemplate, setCurrentTemplate] = useState({
    id: '',
    name: '',
    subject: '',
    htmlContent: '',
    header: '',
    footer: '',
    createdAt: null,
    updatedAt: null
  });
  
  // Editor state
  const [editorContent, setEditorContent] = useState('');
  const [showPreview, setShowPreview] = useState(false);
  const [showSaveModal, setShowSaveModal] = useState(false);
  const [templateName, setTemplateName] = useState('');
  
  // Check admin status
  useEffect(() => {
    const checkAdminStatus = async () => {
      try {
        if (!currentUser) {
          navigate('/');
          return;
        }
        
        const adminDoc = await fires.collection('admin').doc(currentUser.uid).get();
        if (!adminDoc.exists) {
          setIsAdmin(false);
          setLoading(false);
          return;
        }
        
        const adminData = adminDoc.data();
        if (!adminData.roles || !adminData.roles.includes('management')) {
          setIsAdmin(false);
          setLoading(false);
          return;
        }
        
        setIsAdmin(true);
        setLoading(false);
        setError(null);
        loadTemplates();
      } catch (err) {
        console.error('Error checking admin status:', err);
        setError('Failed to verify admin access: ' + err.message);
        setLoading(false);
      }
    };
    
    checkAdminStatus();
  }, [currentUser, navigate]);

  // Load email templates
  const loadTemplates = async () => {
    try {
      const templatesSnapshot = await fires.collection('emailTemplates').orderBy('updatedAt', 'desc').get();
      const templatesData = templatesSnapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      }));
      setTemplates(templatesData);
    } catch (err) {
      console.error('Error loading templates:', err);
      setError('Failed to load email templates: ' + err.message);
    }
  };

  // Create new template
  const createNewTemplate = () => {
    const newTemplate = {
      id: '',
      name: '',
      subject: '',
      htmlContent: getDefaultTemplate(),
      header: getDefaultHeader(),
      footer: getDefaultFooter(),
      createdAt: new Date(),
      updatedAt: new Date()
    };
    setCurrentTemplate(newTemplate);
    setEditorContent(newTemplate.htmlContent);
    setTemplateName('');
  };

  // Load template
  const loadTemplate = (template) => {
    setCurrentTemplate(template);
    setEditorContent(template.htmlContent);
    setTemplateName(template.name);
  };

  // Save template
  const saveTemplate = async () => {
    if (!templateName.trim()) {
      alert('Please enter a template name');
      return;
    }

    setSaving(true);
    try {
      const templateData = {
        name: templateName.trim(),
        subject: currentTemplate.subject,
        htmlContent: editorContent,
        header: currentTemplate.header,
        footer: currentTemplate.footer,
        updatedAt: new Date()
      };

      if (currentTemplate.id) {
        // Update existing template
        await fires.collection('emailTemplates').doc(currentTemplate.id).update(templateData);
        console.log('Template updated successfully');
      } else {
        // Create new template
        templateData.createdAt = new Date();
        const docRef = await fires.collection('emailTemplates').add(templateData);
        setCurrentTemplate({ ...templateData, id: docRef.id });
        console.log('Template created successfully');
      }

      setShowSaveModal(false);
      loadTemplates();
      alert('Template saved successfully!');
    } catch (err) {
      console.error('Error saving template:', err);
      alert('Failed to save template: ' + err.message);
    } finally {
      setSaving(false);
    }
  };

  // Delete template
  const deleteTemplate = async (templateId) => {
    if (window.confirm('Are you sure you want to delete this template?')) {
      try {
        await fires.collection('emailTemplates').doc(templateId).delete();
        loadTemplates();
        if (currentTemplate.id === templateId) {
          createNewTemplate();
        }
        alert('Template deleted successfully!');
      } catch (err) {
        console.error('Error deleting template:', err);
        alert('Failed to delete template: ' + err.message);
      }
    }
  };

  // Preview template
  const previewTemplate = () => {
    setShowPreview(true);
  };

  // Download template as HTML
  const downloadTemplate = () => {
    const fullHtml = `
<!DOCTYPE html>
<html>
<head>
    <meta charset="UTF-8">
    <meta name="viewport" content="width=device-width, initial-scale=1.0">
    <title>${currentTemplate.subject || 'Email Template'}</title>
    <style>
        body { font-family: Arial, sans-serif; margin: 0; padding: 20px; }
        .email-container { max-width: 600px; margin: 0 auto; }
        .header { background-color: #f8f9fa; padding: 20px; text-align: center; }
        .content { padding: 20px; }
        .footer { background-color: #f8f9fa; padding: 20px; text-align: center; font-size: 12px; color: #666; }
    </style>
</head>
<body>
    <div class="email-container">
        <div class="header">${currentTemplate.header}</div>
        <div class="content">${editorContent}</div>
        <div class="footer">${currentTemplate.footer}</div>
    </div>
</body>
</html>`;

    const blob = new Blob([fullHtml], { type: 'text/html' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = `${templateName || 'email-template'}.html`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
  };

  // Default template content
  const getDefaultTemplate = () => {
    return `
<div style="font-family: Arial, sans-serif; line-height: 1.6; color: #333;">
    <h2 style="color: #2c3e50; margin-bottom: 20px;">Welcome to Our Event!</h2>
    
    <p>Dear [Customer Name],</p>
    
    <p>We're excited to have you join us for this amazing event. Here are the details:</p>
    
    <div style="background-color: #f8f9fa; padding: 20px; border-radius: 8px; margin: 20px 0;">
        <h3 style="color: #2c3e50; margin-top: 0;">Event Information</h3>
        <p><strong>Event:</strong> [Event Name]</p>
        <p><strong>Date:</strong> [Event Date]</p>
        <p><strong>Time:</strong> [Event Time]</p>
        <p><strong>Location:</strong> [Event Location]</p>
    </div>
    
    <p>If you have any questions, please don't hesitate to contact us.</p>
    
    <p>Best regards,<br>
    The Event Team</p>
</div>`;
  };

  // Default header
  const getDefaultHeader = () => {
    return `
<div style="text-align: center; padding: 20px; background-color: #2c3e50; color: white;">
    <h1 style="margin: 0; font-size: 28px;">EventGo</h1>
    <p style="margin: 5px 0 0 0; font-size: 14px; opacity: 0.9;">Your Event Management Platform</p>
</div>`;
  };

  // Default footer
  const getDefaultFooter = () => {
    return `
<div style="text-align: center; padding: 20px; background-color: #f8f9fa; color: #666; font-size: 12px;">
    <p style="margin: 0 0 10px 0;">© 2024 EventGo. All rights reserved.</p>
    <p style="margin: 0;">
        <a href="#" style="color: #2c3e50; text-decoration: none; margin: 0 10px;">Privacy Policy</a>
        <a href="#" style="color: #2c3e50; text-decoration: none; margin: 0 10px;">Terms of Service</a>
        <a href="#" style="color: #2c3e50; text-decoration: none; margin: 0 10px;">Contact Us</a>
    </p>
</div>`;
  };

  // TinyMCE configuration
  const editorConfig = {
    height: 500,
    menubar: false,
    plugins: [
      'advlist', 'autolink', 'lists', 'link', 'image', 'charmap', 'preview',
      'anchor', 'searchreplace', 'visualblocks', 'code', 'fullscreen',
      'insertdatetime', 'media', 'table', 'help', 'wordcount'
    ],
    toolbar: 'undo redo | blocks | ' +
      'bold italic backcolor | alignleft aligncenter ' +
      'alignright alignjustify | bullist numlist outdent indent | ' +
      'removeformat | help',
    content_style: 'body { font-family: Arial, sans-serif; font-size: 14px; }',
    setup: (editor) => {
      editor.on('change', () => {
        setEditorContent(editor.getContent());
      });
    }
  };

  if (loading) {
    return (
      <Container className="py-5">
        <div className="text-center">
          <Spinner animation="border" />
          <p className="mt-3">Loading...</p>
        </div>
      </Container>
    );
  }

  if (!isAdmin) {
    return (
      <Container className="py-5">
        <Alert variant="danger">
          <h4>Access Denied</h4>
          <p>You don't have permission to access this page.</p>
          <Button variant="outline-danger" onClick={() => navigate('/')}>
            Go Home
          </Button>
        </Alert>
      </Container>
    );
  }

  return (
    <Container fluid className="py-4">
      <Row>
        <Col>
          <div className="d-flex justify-content-between align-items-center mb-4">
            <div>
              <h2 className="mb-1">Email Template Creator</h2>
              <p className="text-muted mb-0">Create and manage email templates</p>
            </div>
            <div>
              <Button 
                variant="outline-secondary" 
                onClick={() => navigate('/admin-dashboard')}
                className="me-2"
              >
                Back to Dashboard
              </Button>
              <Button 
                variant="primary" 
                onClick={createNewTemplate}
              >
                New Template
              </Button>
            </div>
          </div>

          {error && (
            <Alert variant="danger" className="mb-4">
              {error}
            </Alert>
          )}

          <Row>
            {/* Templates List */}
            <Col md={3}>
              <Card>
                <Card.Header>
                  <h6 className="mb-0">Templates</h6>
                </Card.Header>
                <Card.Body style={{ maxHeight: '600px', overflowY: 'auto' }}>
                  {templates.length === 0 ? (
                    <p className="text-muted text-center">No templates found</p>
                  ) : (
                    <div className="d-grid gap-2">
                      {templates.map(template => (
                        <Button
                          key={template.id}
                          variant={currentTemplate.id === template.id ? 'primary' : 'outline-secondary'}
                          size="sm"
                          onClick={() => loadTemplate(template)}
                          className="text-start"
                        >
                          <div>
                            <div className="fw-bold">{template.name}</div>
                            <small className="opacity-75">
                              {template.updatedAt?.toDate?.()?.toLocaleDateString() || 'Unknown date'}
                            </small>
                          </div>
                        </Button>
                      ))}
                    </div>
                  )}
                </Card.Body>
              </Card>
            </Col>

            {/* Editor */}
            <Col md={9}>
              <Card>
                <Card.Header>
                  <div className="d-flex justify-content-between align-items-center">
                    <h6 className="mb-0">Template Editor</h6>
                    <div>
                      <Button
                        variant="outline-info"
                        size="sm"
                        onClick={previewTemplate}
                        className="me-2"
                      >
                        <FaEye className="me-1" />
                        Preview
                      </Button>
                      <Button
                        variant="outline-success"
                        size="sm"
                        onClick={() => setShowSaveModal(true)}
                        className="me-2"
                      >
                        <FaSave className="me-1" />
                        Save
                      </Button>
                      <Button
                        variant="outline-primary"
                        size="sm"
                        onClick={downloadTemplate}
                      >
                        <FaDownload className="me-1" />
                        Download
                      </Button>
                    </div>
                  </div>
                </Card.Header>
                <Card.Body>
                  <Tabs defaultActiveKey="content" className="mb-3">
                    <Tab eventKey="content" title="Content">
                      <Form.Group className="mb-3">
                        <Form.Label>Subject Line</Form.Label>
                        <Form.Control
                          type="text"
                          placeholder="Enter email subject"
                          value={currentTemplate.subject}
                          onChange={(e) => setCurrentTemplate({...currentTemplate, subject: e.target.value})}
                        />
                      </Form.Group>
                      
                      <Form.Group className="mb-3">
                        <Form.Label>Email Content</Form.Label>
                        
                      </Form.Group>
                    </Tab>
                    
                    <Tab eventKey="header" title="Header">
                      <Form.Group className="mb-3">
                        <Form.Label>Email Header</Form.Label>
                        <Form.Control
                          as="textarea"
                          rows={8}
                          placeholder="Enter header HTML"
                          value={currentTemplate.header}
                          onChange={(e) => setCurrentTemplate({...currentTemplate, header: e.target.value})}
                        />
                      </Form.Group>
                    </Tab>
                    
                    <Tab eventKey="footer" title="Footer">
                      <Form.Group className="mb-3">
                        <Form.Label>Email Footer</Form.Label>
                        <Form.Control
                          as="textarea"
                          rows={8}
                          placeholder="Enter footer HTML"
                          value={currentTemplate.footer}
                          onChange={(e) => setCurrentTemplate({...currentTemplate, footer: e.target.value})}
                        />
                      </Form.Group>
                    </Tab>
                  </Tabs>
                </Card.Body>
              </Card>
            </Col>
          </Row>
        </Col>
      </Row>

      {/* Save Modal */}
      <Modal show={showSaveModal} onHide={() => setShowSaveModal(false)}>
        <Modal.Header closeButton>
          <Modal.Title>Save Template</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <Form.Group>
            <Form.Label>Template Name</Form.Label>
            <Form.Control
              type="text"
              placeholder="Enter template name"
              value={templateName}
              onChange={(e) => setTemplateName(e.target.value)}
            />
          </Form.Group>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setShowSaveModal(false)}>
            Cancel
          </Button>
          <Button variant="primary" onClick={saveTemplate} disabled={saving}>
            {saving ? <Spinner size="sm" className="me-2" /> : null}
            Save Template
          </Button>
        </Modal.Footer>
      </Modal>

      {/* Preview Modal */}
      <Modal show={showPreview} onHide={() => setShowPreview(false)} size="lg">
        <Modal.Header closeButton>
          <Modal.Title>Template Preview</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <div 
            dangerouslySetInnerHTML={{
              __html: `
                <div style="max-width: 600px; margin: 0 auto; border: 1px solid #ddd;">
                  ${currentTemplate.header}
                  <div style="padding: 20px;">
                    ${editorContent}
                  </div>
                  ${currentTemplate.footer}
                </div>
              `
            }}
          />
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setShowPreview(false)}>
            Close
          </Button>
        </Modal.Footer>
      </Modal>
    </Container>
  );
}
